import React, { useState, useCallback, useEffect } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import { Delete } from 'lucide-react';

const SYMBOL_TO_OP = { '\u00d7': '*', '\u00f7': '/', '\u2212': '-' };

const normalizeExpression = (raw) => {
  let s = raw;
  Object.entries(SYMBOL_TO_OP).forEach(([symbol, op]) => {
    s = s.split(symbol).join(op);
  });
  return s;
};

const safeEval = (expr) => {
  try {
    // eslint-disable-next-line no-new-func
    const result = Function(`'use strict'; return (${expr})`)();
    if (!isFinite(result)) return null;
    return Math.round(result * 1e10) / 1e10;
  } catch {
    return null;
  }
};

const BasicCalculator = () => {
  const [display, setDisplay] = useState('0');
  const [expression, setExpression] = useState('');
  const [memory, setMemory] = useState(0);
  const [justEvaluated, setJustEvaluated] = useState(false);

  const inputDigit = useCallback((d) => {
    if (justEvaluated) {
      setDisplay(String(d));
      setJustEvaluated(false);
    } else {
      setDisplay((prev) => (prev === '0' ? String(d) : prev + d));
    }
  }, [justEvaluated]);

  const inputDot = useCallback(() => {
    if (justEvaluated) {
      setDisplay('0.');
      setJustEvaluated(false);
    } else {
      setDisplay((prev) => (prev.includes('.') ? prev : prev + '.'));
    }
  }, [justEvaluated]);

  const clearAll = useCallback(() => {
    setDisplay('0');
    setExpression('');
    setJustEvaluated(false);
  }, []);

  const clearEntry = useCallback(() => setDisplay('0'), []);

  const backspace = useCallback(() => {
    if (justEvaluated) {
      setDisplay('0');
      setExpression('');
      setJustEvaluated(false);
    } else {
      setDisplay((prev) => (prev.length > 1 ? prev.slice(0, -1) : '0'));
    }
  }, [justEvaluated]);

  const operator = useCallback((op) => {
    setExpression((prevExpr) => `${prevExpr}${display} ${op} `);
    setDisplay('0');
    setJustEvaluated(false);
  }, [display]);

  const evaluate = useCallback(() => {
    const full = normalizeExpression(`${expression}${display}`);
    const result = safeEval(full);
    if (result === null) {
      setDisplay('Error');
    } else {
      setDisplay(String(result));
      setJustEvaluated(true);
    }
    setExpression('');
  }, [expression, display]);

  const percent = useCallback(() => {
    setDisplay((prev) => String(parseFloat(prev) / 100));
  }, []);

  const toggleSign = useCallback(() => {
    setDisplay((prev) => {
      if (prev === '0') return prev;
      return prev.startsWith('-') ? prev.slice(1) : '-' + prev;
    });
  }, []);

  const reciprocal = useCallback(() => {
    setDisplay((prev) => String(1 / parseFloat(prev)));
  }, []);

  const memoryAction = useCallback((action) => {
    const val = parseFloat(display);
    if (action === 'MC') setMemory(0);
    else if (action === 'MR') {
      setDisplay(String(memory));
      setJustEvaluated(true);
    } else if (action === 'M+') setMemory((m) => m + val);
    else if (action === 'M-') setMemory((m) => m - val);
    else if (action === 'MS') setMemory(val);
  }, [display, memory]);

  const handleKey = useCallback((e) => {
    const { key } = e;
    if (key >= '0' && key <= '9') return inputDigit(key);
    if (key === '.') return inputDot();
    if (key === '+' || key === '-') return operator(key);
    if (key === '*') return operator('×');
    if (key === '/') { e.preventDefault(); return operator('÷'); }
    if (key === 'Enter' || key === '=') { e.preventDefault(); return evaluate(); }
    if (key === 'Escape') return clearAll();
    if (key === 'Backspace') return backspace();
    if (key === '%') return percent();
  }, [inputDigit, inputDot, operator, evaluate, clearAll, backspace, percent]);

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  return (
    <CalculatorLayout
      title="Basic Calculator"
      subtitle="Standard arithmetic with memory functions. Use keyboard or click buttons."
      category="Math"
      description={<>
        <p>This basic calculator performs standard arithmetic operations: addition, subtraction, multiplication, and division. It also supports memory keys (MC, MR, M+, M-, MS) to store and recall values.</p>
        <p>You can use your <strong>keyboard</strong>: numbers, <code>+ - * /</code>, <code>Enter</code> for equals, <code>Esc</code> to clear, and <code>Backspace</code> to undo.</p>
      </>}
    >
      <div className="max-w-md mx-auto">
        <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 p-4 mb-4">
          <div className="display-font text-right text-xs text-slate-400 h-5 overflow-hidden truncate" title={expression}>
            {expression || (memory !== 0 ? `M = ${memory}` : ' ')}
          </div>
          <div className="display-font text-right text-3xl md:text-4xl font-semibold text-bharat-navy truncate">
            {display}
          </div>
        </div>

        <div className="grid grid-cols-5 gap-2 mb-2">
          <CalcButton label="MC" onClick={() => memoryAction('MC')} variant="mem" />
          <CalcButton label="MR" onClick={() => memoryAction('MR')} variant="mem" />
          <CalcButton label="M+" onClick={() => memoryAction('M+')} variant="mem" />
          <CalcButton label="M-" onClick={() => memoryAction('M-')} variant="mem" />
          <CalcButton label="MS" onClick={() => memoryAction('MS')} variant="mem" />
        </div>

        <div className="grid grid-cols-4 gap-2">
          <CalcButton label="AC" onClick={clearAll} variant="fn" />
          <CalcButton label="CE" onClick={clearEntry} variant="fn" />
          <CalcButton label={<Delete className="w-5 h-5" />} onClick={backspace} variant="fn" />
          <CalcButton label="÷" onClick={() => operator('÷')} variant="op" />

          <CalcButton label="7" onClick={() => inputDigit(7)} />
          <CalcButton label="8" onClick={() => inputDigit(8)} />
          <CalcButton label="9" onClick={() => inputDigit(9)} />
          <CalcButton label="×" onClick={() => operator('×')} variant="op" />

          <CalcButton label="4" onClick={() => inputDigit(4)} />
          <CalcButton label="5" onClick={() => inputDigit(5)} />
          <CalcButton label="6" onClick={() => inputDigit(6)} />
          <CalcButton label="−" onClick={() => operator('-')} variant="op" />

          <CalcButton label="1" onClick={() => inputDigit(1)} />
          <CalcButton label="2" onClick={() => inputDigit(2)} />
          <CalcButton label="3" onClick={() => inputDigit(3)} />
          <CalcButton label="+" onClick={() => operator('+')} variant="op" />

          <CalcButton label="±" onClick={toggleSign} variant="fn" />
          <CalcButton label="0" onClick={() => inputDigit(0)} />
          <CalcButton label="." onClick={inputDot} />
          <CalcButton label="=" onClick={evaluate} variant="eq" />
        </div>

        <div className="grid grid-cols-2 gap-2 mt-2">
          <CalcButton label="%" onClick={percent} variant="fn" />
          <CalcButton label="1/x" onClick={reciprocal} variant="fn" />
        </div>
      </div>
    </CalculatorLayout>
  );
};

const BUTTON_VARIANTS = {
  num: 'bg-white border-slate-200 text-bharat-navy hover:bg-slate-50 shadow-sm',
  op: 'bg-slate-100 border-slate-200 text-bharat-navy hover:bg-slate-200',
  eq: 'bg-saffron-500 border-saffron-500 text-white hover:bg-saffron-600 shadow-sm',
  fn: 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100 text-sm',
  mem: 'bg-indgreen-50 border-indgreen-200 text-indgreen-700 hover:bg-indgreen-100 text-sm',
};

const CalcButton = ({ label, onClick, variant = 'num' }) => {
  const base = 'calc-btn h-14 md:h-16 rounded-lg font-display font-semibold text-lg md:text-xl flex items-center justify-center border';
  return (
    <button onClick={onClick} className={`${base} ${BUTTON_VARIANTS[variant]}`}>
      {label}
    </button>
  );
};

export default BasicCalculator;
